import { stringify } from 'query-string';
import set from 'lodash/set';
import Router from 'next/router';
import pickBy from 'lodash/pickBy';
import isNull from 'lodash/isNull';
import { designerMode, productMode, storeMode } from './productListModes';
import { SizeData } from '../common/models/SizeData';
import Departments from '../common/models/Departments';

const SEARCH_ENDPOINT = 'api/search_products/';
const BRAND_SEARCH_ENDPOINT = 'api/brand_products/';
const STORE_SEARCH_ENDPOINT = 'api/store_search_products/';

const PAGE_BRAND_DETAIL = 'BrandDetail';
const PAGE_SEARCH = 'Search';

let searchStore;

function generateQueryString({ brandsIds, categoryId, department, seasons, sizes, sortKey, offset } = {}) {
  const { api, isFiltersModalVisible } = searchStore.getState();
  const { onSale, page, query, removeQuery } = api;

  const split = query || '';
  const q = removeQuery ? '' : split;

  let finalSizes = sizes;

  const sizesFilterActive = sizes && sizes.length > 0;
  const isShoes = sizesFilterActive && sizes[0].toLowerCase().includes('shoe');
  // add universal size if sizes filter are active
  if (!isShoes && sizesFilterActive && !finalSizes.some(el => el === SizeData.universal())) {
    finalSizes.push(SizeData.universal());
  }

  // remove universal size if U is the only size selected
  if (sizes && finalSizes.filter(el => SizeData.universal() === el).length === sizes.length) {
    finalSizes = [];
  }

  const queryObj = {};
  set(queryObj, 'on_sale', onSale ? '1' : null);
  set(queryObj, 'categories', categoryId ? [categoryId] : null);
  set(queryObj, 'brands', brandsIds || null);
  set(queryObj, 'seasons', seasons || null);
  set(queryObj, 'sizes', finalSizes || null);
  set(queryObj, 'page', page || null);
  set(queryObj, 'sort_field', sortKey || null);
  set(queryObj, 'q', q || '');
  set(queryObj, 'limit', '60');
  set(queryObj, 'skip', offset || '0');
  if (isFiltersModalVisible) {
    set(queryObj, 'filters', 'true');
  }

  const catStringUrl = stringify(pickBy(queryObj, value => !isNull(value)), { arrayFormat: 'bracket', sort: false });
  return `?${catStringUrl.replace(/\[\]/g, '%5B%5D')}`;
}

function generateFilterUrl({
  brandsIds,
  department = null,
  categoryId,
  seasons,
  sizes,
  sortKey,
  offset = 0,
  productListMode,
  storeName = null,
  storeId = null,
} = {}) {
  let baseUrl;

  switch (productListMode) {
    case productMode: {
      baseUrl = `${SEARCH_ENDPOINT}${categoryId || department}`;
      break;
    }
    case designerMode: {
      baseUrl = `${BRAND_SEARCH_ENDPOINT}${brandsIds[0]}${department ? `/${department}` : ''}`;
      break;
    }
    case storeMode: {
      let departmentName = '';
      if (department === Departments.getKidsInternalName()) {
        departmentName = `/${department}`;
      }
      baseUrl = `${STORE_SEARCH_ENDPOINT}${storeName}/${storeId}${departmentName}`;
      break;
    }
  }

  const x = `${baseUrl}${generateQueryString({
    brandsIds,
    categoryId,
    department,
    seasons,
    sizes,
    sortKey,
    offset,
  })}`;
  return x;
}

function getComponentName(mode) {
  switch (mode) {
    case designerMode:
      return 'Designer';
    case storeMode:
      return 'StoreProductList';
    default:
      return 'ProductList';
  }
}

function generateHistoryUrl(addQuery, productListMode, countryCode) {
  const { brandUrl, categoryUrl, filterSettings, sortFilterSelect, offset } = searchStore.getState().api;
  const { department } = searchStore.getState();
  const { brandsIds, categoryId, seasons, sizes, storeName, storeId } = filterSettings;

  const queryString = generateQueryString({
    brandsIds,
    categoryId,
    department,
    seasons,
    sizes,
    sortKey: sortFilterSelect,
    offset,
  });

  const page = getComponentName(productListMode);

  if (productListMode === designerMode) {
    return {
      as: `/${countryCode}${brandUrl}/${department}/${addQuery ? queryString : ''}`,
      queryString,
      page,
    };
  }
  if (productListMode === storeMode) {
    return {
      as: `/${countryCode}/stores/${storeName}/${storeId}/${department ? `${department}/` : ''}${
        addQuery ? queryString : ''
      }`,
      queryString,
      page,
    };
  }

  return {
    as: `/${countryCode}${categoryUrl}/${addQuery ? queryString : ''}`,
    queryString,
    page,
  };
}

const setSearchStore = store => {
  searchStore = store;
};

function getTopLevelDomain() {
  return getTopLevelDomainOf(window.location);
}

function getTopLevelDomainOf(urlString) {
  const url = new URL(urlString);

  let topLevelDomain;

  if (url.hostname === 'localhost') {
    return url.hostname;
  }
  topLevelDomain = url.hostname.match(/[-\w]+\.(?:[-\w]+\.xn--[-\w]+|[-\w]{3,}|[-\w]+\.[-\w]{2})$/i);
  if (topLevelDomain !== null) {
    [topLevelDomain] = topLevelDomain;
  }
  return topLevelDomain;
}

function changeUrlFiltersVisibility(visible, mode) {
  let {
    location: { search },
  } = window;
  const filterIdx = search.search(/[?&]+filters=true/);
  if (visible && filterIdx === -1) {
    const delimiter = search ? '&' : '?';
    search += `${delimiter}filters=true`;
  } else if (!visible && filterIdx !== -1) {
    search = search.substr(0, filterIdx) + search.substr(filterIdx + 13);
  }
  Router.push(`/${getComponentName(mode)}${search}`, window.location.pathname + search, { shallow: true });
}

export {
  changeUrlFiltersVisibility,
  setSearchStore,
  generateFilterUrl,
  generateHistoryUrl,
  getTopLevelDomain,
  getTopLevelDomainOf,
  SEARCH_ENDPOINT,
  BRAND_SEARCH_ENDPOINT,
  STORE_SEARCH_ENDPOINT,
  PAGE_BRAND_DETAIL,
  PAGE_SEARCH,
};
